import React, { memo, useEffect, useMemo, useState } from "react";
import {
	FaCheck,
	FaChevronRight,
	FaFileCirclePlus,
	FaFloppyDisk,
	FaPaperPlane,
	FaTrashCan,
	FaXmark,
} from "react-icons/fa6";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import "./draft-list.style.scss";

// components
import DraftItem from "./draft-item.component";
import type { DraftListEntry } from "./draft-item.component";
import DraftSearchBox from "./draft-search-box.component";
import EmptyRecord from "../empty-record/empty-record.component";
import FormError from "../form-error/form-error.component";
import InlineNotice from "../inline-notice/inline-notice.component";
import { Button } from "../ui/button";

import { useLocalDrafts } from "../../context/local-drafts.context";
import { useDebounce } from "../../hooks/useDebounce";
import { filterDraftsBySearch } from "./draft-search.util";

// queries
import {
	useDrafts,
	useSaveDraft,
	useDeleteDraft,
	deriveDraftLists,
} from "../../queries/drafts.queries";
import type {
	SaveDraftVars,
	DeleteDraftVars,
} from "../../queries/drafts.queries";
import type { InvoiceFormValues } from "../invoice-form/invoice-form.types";

interface DraftListProps {
	currentDraft: (Partial<InvoiceFormValues> & Record<string, unknown>) | null;
	onSetCurrent: (draft: DraftListEntry) => void;
	unsavedInvoiceNumber: string;
	viewVisibleList: "draft" | "ready";
	setViewVisibleList: () => void;
}

function DraftList({
	currentDraft,
	onSetCurrent,
	unsavedInvoiceNumber,
	viewVisibleList,
	setViewVisibleList,
}: DraftListProps) {
	const { data } = useDrafts();
	const { draftsList } = deriveDraftLists(data);
	const { localDraftsList, addLocalDraft, setLocalDraftsList } =
		useLocalDrafts();

	const saveDraftMutation = useSaveDraft();
	const deleteDraftMutation = useDeleteDraft();
	const isLoading =
		saveDraftMutation.isPending || deleteDraftMutation.isPending;

	const [drafts, setDrafts] = useState<DraftListEntry[]>([]);

	// Bulk-select is an explicit mode (a "Select" button turns it on), not an
	// always-on checkbox per row — entering it is what reveals checkboxes and
	// the action toolbar, so neither exists to get in the way of the default
	// click-a-row-to-open-it flow.
	const [isSelectMode, setIsSelectMode] = useState(false);
	const [selectedItems, setSelectedItems] = useState<Set<DraftListEntry>>(
		new Set(),
	);

	// Replaces showToastMessage for both bulk-action success confirmations and
	// client-side selection-filter feedback (no server round-trip on the
	// latter) — a single slot since only one of these is ever relevant at a time.
	const [notice, setNotice] = useState<{
		message: string;
		type: "info" | "success";
	} | null>(null);

	useEffect(() => {
		const totalDrafts = [
			...draftsList,
			...localDraftsList,
		] as DraftListEntry[];
		setDrafts(totalDrafts);
	}, [draftsList, localDraftsList]);

	const [searchQuery, setSearchQuery] = useState("");
	const debouncedQuery = useDebounce(searchQuery, 150);

	// Keeps the draft currently open for editing visible even if a search
	// query would otherwise filter it out — the form panel stays bound to it
	// regardless, so hiding its list row would only be confusing.
	const filteredDrafts = useMemo(() => {
		const filtered = filterDraftsBySearch(drafts, debouncedQuery);
		if (debouncedQuery.trim() === "" || unsavedInvoiceNumber === "")
			return filtered;

		const isEditingDraftVisible = filtered.some(
			(item) => item.invoice_number === unsavedInvoiceNumber,
		);
		if (isEditingDraftVisible) return filtered;

		const editingDraft = drafts.find(
			(item) => item.invoice_number === unsavedInvoiceNumber,
		);
		return editingDraft ? [editingDraft, ...filtered] : filtered;
	}, [drafts, debouncedQuery, unsavedInvoiceNumber]);

	const handleCreateDraft = async () => {
		const dummyDraft: DraftListEntry = {
			invoice_number: `DRF_${uuidv4()}`,
			invoice_date: moment().format("YYYY-MM-DD"),
		};
		addLocalDraft(dummyDraft);
		onSetCurrent(dummyDraft);
	};

	const toggleSelection = (item: DraftListEntry) => {
		setSelectedItems((prev) => {
			const next = new Set(prev);
			if (next.has(item)) {
				next.delete(item);
			} else {
				next.add(item);
			}
			return next;
		});
	};

	const allSelected =
		filteredDrafts.length > 0 &&
		selectedItems.size === filteredDrafts.length;
	const handleToggleSelectAll = () => {
		setSelectedItems(allSelected ? new Set() : new Set(filteredDrafts));
	};

	const clearSelection = () => setSelectedItems(new Set());

	const handleEnterSelectMode = () => setIsSelectMode(true);
	const handleExitSelectMode = () => {
		setIsSelectMode(false);
		clearSelection();
	};

	// Eligibility is derived straight from the current selection on every
	// render instead of hand-maintained running counters (the previous
	// approach) — no risk of the tallies drifting out of sync with what's
	// actually selected, and no need to block selecting an "ineligible" item
	// in the first place. Bulk Save/Ready simply act on whichever subset of
	// the selection qualifies and report what they skipped.
	const eligibleForSave = useMemo(
		() =>
			[...selectedItems].filter(
				(item) =>
					!item.hasOwnProperty("draft_bill_status") &&
					item.hasOwnProperty("draft_valid"),
			),
		[selectedItems],
	);
	const eligibleForReady = useMemo(
		() => [...selectedItems].filter((item) => !!item.draft_valid),
		[selectedItems],
	);

	// to reset selection if after selection, user starts editing a draft
	useEffect(() => {
		clearSelection();
	}, [unsavedInvoiceNumber]);

	// bulk draft add — runs only on the subset of the selection that's a
	// local, touched-at-least-once draft not yet saved to the DB; anything
	// else selected (already-saved drafts, untouched blank shells) is left
	// alone and called out in the notice.
	const handleBulkDraftSave = async () => {
		if (isLoading || eligibleForSave.length === 0) return;

		const saveDrafts: SaveDraftVars = {
			drafts: eligibleForSave,
			draft_bill_status: "draft",
		};
		try {
			await saveDraftMutation.mutateAsync(saveDrafts);
			const savedSet = new Set(eligibleForSave);
			const newLocalDrafts = localDraftsList.filter(
				(localDraft: DraftListEntry) => !savedSet.has(localDraft),
			);
			setLocalDraftsList(newLocalDrafts);

			const skipped = selectedItems.size - eligibleForSave.length;
			setNotice({
				message:
					skipped > 0
						? `Saved ${eligibleForSave.length} draft(s), skipped ${skipped} (already saved or not started)`
						: `Saved ${eligibleForSave.length} draft(s)`,
				type: "success",
			});
		} catch {
			// FormError (bound to saveDraftMutation) renders the message
		}

		handleExitSelectMode();
		onSetCurrent({});
	};

	// bulk draft update — runs only on the subset of the selection that's
	// actually validated; unvalidated or untouched items are left as-is.
	const handleBulkDraftReady = async () => {
		if (isLoading || eligibleForReady.length === 0) return;

		const readyDrafts: SaveDraftVars = {
			drafts: eligibleForReady,
			draft_bill_status: "ready",
		};
		try {
			await saveDraftMutation.mutateAsync(readyDrafts);
			const readySet = new Set(eligibleForReady);
			const newLocalDrafts = localDraftsList.filter(
				(localDraft: DraftListEntry) => !readySet.has(localDraft),
			);
			setLocalDraftsList(newLocalDrafts);

			const skipped = selectedItems.size - eligibleForReady.length;
			setNotice({
				message:
					skipped > 0
						? `Marked ${eligibleForReady.length} draft(s) ready, skipped ${skipped} (not validated)`
						: `Marked ${eligibleForReady.length} draft(s) ready`,
				type: "success",
			});
		} catch {
			// FormError (bound to saveDraftMutation) renders the message
		}

		handleExitSelectMode();
		onSetCurrent({});
	};

	// bulk or individual draft Delete — unlike Save/Ready, delete applies to
	// the whole selection regardless of type/validity, so there's nothing to
	// split.
	const handleDeleteDraft = async () => {
		if (isLoading || selectedItems.size === 0) return;
		const selectedDrafts = [...selectedItems];

		// filter out drafts to be deleted and set localDrafts to new after deletion
		const newLocalDrafts = localDraftsList.filter(
			(localDraft: DraftListEntry) => !selectedItems.has(localDraft),
		);
		setLocalDraftsList(newLocalDrafts);

		// filter drafts to be deleted and send to be deleted drafts to backend for deletion
		const selectedDraftsList: DraftListEntry[] = [];

		for (const item of selectedDrafts) {
			if ("draft_bill_status" in item) {
				selectedDraftsList.push(item);
			}
		}
		if (
			Array.isArray(selectedDraftsList) &&
			selectedDraftsList.length > 0
		) {
			const deleteVars: DeleteDraftVars = { drafts: selectedDraftsList };
			try {
				await deleteDraftMutation.mutateAsync(deleteVars);
				setNotice({ message: "Draft(s) deleted", type: "success" });
			} catch {
				// FormError (bound to deleteDraftMutation) renders the message
			}
		}

		handleExitSelectMode();
		onSetCurrent({});
	};

	return (
		<>
			{viewVisibleList === "ready" && (
				<div className="draftButtonPannel" onClick={setViewVisibleList}>
					<div className="section-title-text">Drafts</div>
					<div>{drafts.length}</div>
					<div>Open Panel</div>
					<div className="openPanel">
						<FaChevronRight size="8em" />
					</div>
				</div>
			)}
			<div
				className={`list ${
					viewVisibleList === "ready" ? "dsp_hidden" : "dsp_shown"
				}`}
			>
				<div className="drafts-invoices-container">
					<div className="drafts-invoices-header">
						<div className="di-header-title">
							<div className="section-title-text">
								Invoice Draft
							</div>
						</div>
						{drafts.length > 0 && (
							<DraftSearchBox
								value={searchQuery}
								onChange={setSearchQuery}
								placeholder="Search by customer or invoice #"
								resultCount={filteredDrafts.length}
								totalCount={drafts.length}
							/>
						)}
						{!isLoading && drafts.length > 0 && (
							<div className="drafts-action">
								{!isSelectMode ? (
									<Button
										variant="outline"
										size="sm"
										onClick={handleEnterSelectMode}
									>
										Select
									</Button>
								) : (
									<>
										<div
											className="select-all-toggle"
											onClick={handleToggleSelectAll}
										>
											<div
												className={`draft-select ${
													allSelected && "selected"
												}`}
											>
												{allSelected && (
													<FaCheck
														style={{
															color: "#46BE46",
															fontSize: "12px",
														}}
													/>
												)}
											</div>
											<span>
												{selectedItems.size > 0
													? `${selectedItems.size} selected`
													: "Select all"}
											</span>
										</div>
										<div className="drafts-action-btns">
											<Button
												variant="destructive"
												size="sm"
												disabled={
													selectedItems.size === 0
												}
												onClick={handleDeleteDraft}
											>
												<FaTrashCan /> Delete
											</Button>
											{eligibleForSave.length > 0 && (
												<Button
													variant="outline"
													size="sm"
													onClick={
														handleBulkDraftSave
													}
												>
													<FaFloppyDisk /> Save
													{eligibleForSave.length <
														selectedItems.size &&
														` (${eligibleForSave.length})`}
												</Button>
											)}
											{eligibleForReady.length > 0 && (
												<Button
													size="sm"
													onClick={
														handleBulkDraftReady
													}
												>
													<FaPaperPlane /> Ready
													{eligibleForReady.length <
														selectedItems.size &&
														` (${eligibleForReady.length})`}
												</Button>
											)}
											<Button
												variant="ghost"
												size="sm"
												onClick={handleExitSelectMode}
											>
												<FaXmark /> Cancel
											</Button>
										</div>
									</>
								)}
							</div>
						)}
					</div>

					<div className="draft-mutation-message">
						<FormError
							isError={
								saveDraftMutation.isError ||
								deleteDraftMutation.isError
							}
							error={
								saveDraftMutation.error ||
								deleteDraftMutation.error
							}
							fallback="Something went wrong. Please try again."
						/>
						<InlineNotice
							message={notice?.message ?? null}
							type={notice?.type}
							autoHideMs={3000}
							onDismiss={() => setNotice(null)}
						/>
					</div>
					<div className="drafts-invoices-list">
						{drafts?.length > 0 ? (
							<>
								{filteredDrafts.length > 0 ? (
									filteredDrafts.map((draft, i) => (
										<DraftItem
											key={i}
											draft={draft}
											isSelected={selectedItems.has(
												draft,
											)}
											isSelectMode={isSelectMode}
											toggleSelection={toggleSelection}
											currentDraft={currentDraft}
											onSetCurrent={onSetCurrent}
											unsavedInvoiceNumber={
												unsavedInvoiceNumber
											}
											searchQuery={debouncedQuery}
										/>
									))
								) : (
									<EmptyRecord
										caption={`No drafts match "${debouncedQuery}"`}
									/>
								)}
								<div className="new-draft-btn">
									<div
										className="new-draft-btn-inner"
										onClick={handleCreateDraft}
									>
										<FaFileCirclePlus />
										Generate New Draft
									</div>
								</div>
							</>
						) : (
							<div className="new-draft-btn">
								<div
									className="new-draft-btn-inner"
									onClick={handleCreateDraft}
								>
									<FaFileCirclePlus />
									Generate New Draft
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
}

export default memo(DraftList);
